import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { ReactNode, useState } from "react";

import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { ChoosingModal } from "../../../components/ui-kit/choosing-modal/choosing-modal";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { SelectionMode } from "../../../models/data-model";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { AdminConsole } from "../admin-console";
import { AddClinicForm } from "../clinics/add-clinic-form";
import { Clinics } from "../clinics/clinics";

export const ClinicsModal = observer(function ClinicsModal(props: {
  model: Clinics;
  mode: SelectionMode;
  preselected?: string[];
  filter?: Record<string, any[]>;
  title: ReactNode;
  // Not `Promise<void>`: the list path goes through `ChoosingModal`, which
  // fires this and closes without awaiting, so only one of the two paths could
  // ever honour it.
  onConfirm: (params: { selected: any[] }) => void;
  onClose: () => void;
  /** Offer a "New clinic" form for when the clinic to pick does not exist yet. */
  allowCreate?: boolean;
  /** Required by {@link allowCreate} — the form posts through it. */
  admin?: AdminConsole;
  onAfterCreate?: () => Promise<void> | void;
}) {
  const { t } = useLingui();
  const countries = useCountries();
  const [view, setView] = useState<"list" | "create">("list");
  const [saving, setSaving] = useState(false);
  const initialColumnFilters = props.filter?.country?.length
    ? [{ id: "country", value: props.filter.country }]
    : [];

  const canCreate = props.allowCreate && props.admin;

  return (
    <ChoosingModal
      model={props.model}
      // Guards Escape, the overlay and Cancel alike - they all route here.
      onClose={() => !saving && props.onClose()}
      onConfirm={props.onConfirm}
      title={view === "list" ? props.title : <Trans>Add new clinic</Trans>}
      fillHeight
      // The create form brings its own submit and Cancel, so the modal's
      // Confirm / Cancel footer would only be a second, conflicting pair.
      readOnly={view === "create"}
      ownSections={view === "create"}
      table={
        view === "create" && props.admin ? (
          <AddClinicForm
            admin={props.admin}
            compact
            onCancel={() => setView("list")}
            onSuccess={async (uuid) => {
              // Dismissing mid-flight would otherwise still land the pick
              // after the admin thought they had backed out.
              setSaving(true);
              try {
                await props.onAfterCreate?.();
                if (props.mode === "single") {
                  props.model.selected = [uuid];
                } else if (!props.model.selected.includes(uuid)) {
                  props.model.selected.push(uuid);
                }
                props.onConfirm({ selected: [...props.model.selected] });
                // Not the guarded close above - that one is still blocked.
                props.onClose();
              } finally {
                setSaving(false);
              }
            }}
          />
        ) : (
          <DataTable
            model={props.model}
            itemKeyField="uuid"
            isLoading={props.model.loading}
            fillHeight
            canSelect={props.mode}
            preselected={props.preselected}
            filterNoun={(count) =>
              t`${plural(count, { one: "clinic", other: "clinics" })}`
            }
            filterActions={
              canCreate ? (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => setView("create")}
                >
                  <Trans>New clinic</Trans>
                </Button>
              ) : undefined
            }
            initialColumnFilters={initialColumnFilters}
            columns={[
              {
                header: <Trans>Name</Trans>,
                attr: "name",
                type: "string",
                sort: true,
                initSort: "asc",
                align: "left",
              },
              {
                header: <Trans>Ambulance</Trans>,
                attr: "ambulance",
                type: "string",
                align: "left",
              },
              {
                header: <Trans>Country</Trans>,
                attr: "country",
                type: "string",
                align: "left",
                filter: {
                  kind: "multi",
                  group: t`Location`,
                  options: getCountryOptions,
                },
              },
              {
                header: <Trans>Region</Trans>,
                attr: "region",
                align: "left",
                filter: {
                  kind: "multi",
                  group: t`Location`,
                  options: (table) => getRegionOptions(table, countries),
                  disabled: regionDisabled,
                  disabledMessage: t`Select a country first`,
                  predicate: regionPredicate,
                },
                cell: ({ item }) =>
                  item.region != null
                    ? countries.getRegionSchema(item.country, item.region)
                        ?.region_name
                    : undefined,
              },
              {
                header: <Trans># of physicians</Trans>,
                width: 30,
                cell: ({ item }) => item.numPhysicians,
              },
            ]}
          />
        )
      }
    />
  );
});
